import { createDecorator, EnderecoFieldGroupModel, MunicipioSelectModel, SimNaoEnum } from 'components/form'
import { resolveValue } from 'components/form/core/hooks/useField'
import { EnderecoIndigenaFieldGroupModel } from 'components/form/field/enderecoindigena/model-enderecoIndigena'
import { AldeiaSelectFieldModel } from 'components/form/field/select/AldeiaSelectField'
import { DseiSelectFieldModel } from 'components/form/field/select/DseiSelectField'
import { PaisSelectFieldModel } from 'components/form/field/select/PaisSelectField'
import { PoloBaseSelectFieldModel } from 'components/form/field/select/PoloBaseSelectField'
import { RacaCorSelectModel } from 'components/form/field/select/RacaCorSelectField'
import { differenceInYears } from 'date-fns'
import { Calculation } from 'final-form-calculate'
import {
  NacionalidadeEnum,
  RacaCorDbEnum,
  SexoEnum,
  TipoEnderecoEnum,
  TipoLocalNascimentoEnum,
} from 'graphql/types.generated'
import { MetaRoot } from 'util/metaPath'
import { tipoEnderecoIndigena } from 'view/cadastro-imovel/model-cadastroImovel'

import { CODIGO_PAIS_BRASIL } from '../constants-cidadao'
import { isCidadaoEntre12E21Anos } from '../utils-cidadao'
import { CidadaoFormModel } from './types/model-cidadao'

const calculateIsResponsavelFamiliar = (allValues: CidadaoFormModel, cpfOverride?: string, cnsOverride?: string) => {
  const cpf = cpfOverride ?? allValues?.dadosPessoais?.cpf
  const cns = cnsOverride ?? allValues?.dadosPessoais?.cns
  const hasCpfOrCns = !!cpf || !!cns
  return hasCpfOrCns ? allValues?.responsabilidadeAcompanhamento?.isResponsavelFamiliar : false
}

const createCpfCalculator = (meta: MetaRoot<CidadaoFormModel>): Calculation[] => [
  {
    field: meta?.dadosPessoais?.stNaoPossuiCpf.absolutePath(),
    updates: {
      [meta?.dadosPessoais?.cpf?.absolutePath()]: (stNaoPossuiCpf: boolean, allValues: CidadaoFormModel) =>
        stNaoPossuiCpf ? null : allValues?.dadosPessoais?.cpf,
      [meta?.dadosPessoais?.justificativaNaoPossuiCpf?.absolutePath()]: (
        stNaoPossuiCpf: boolean,
        allValues: CidadaoFormModel
      ) => (!stNaoPossuiCpf ? null : allValues?.dadosPessoais?.justificativaNaoPossuiCpf),
      [meta?.responsabilidadeAcompanhamento.isResponsavelFamiliar.absolutePath()]: (
        stNaoPossuiCpf: boolean,
        allValues: CidadaoFormModel
      ) => calculateIsResponsavelFamiliar(allValues, stNaoPossuiCpf ? undefined : allValues?.dadosPessoais?.cpf),
    },
  },
  {
    field: meta?.dadosPessoais?.cpf.absolutePath(),
    updates: {
      [meta?.responsabilidadeAcompanhamento.isResponsavelFamiliar.absolutePath()]: (
        cpf: string,
        allValues: CidadaoFormModel
      ) => calculateIsResponsavelFamiliar(allValues, cpf),
    },
  },
]

const createCnsCalculator = (meta: MetaRoot<CidadaoFormModel>): Calculation[] => [
  {
    field: meta?.dadosPessoais?.cns.absolutePath(),
    updates: {
      [meta?.responsabilidadeAcompanhamento.isResponsavelFamiliar.absolutePath()]: (
        cns: string,
        allValues: CidadaoFormModel
      ) => calculateIsResponsavelFamiliar(allValues, undefined, cns),
    },
  },
]

const createEtniaCalculator = (meta: MetaRoot<CidadaoFormModel>): Calculation[] => [
  {
    field: meta?.dadosPessoais?.racaCor.absolutePath(),
    updates: {
      [meta?.dadosPessoais?.etnia?.absolutePath()]: (racaCor: RacaCorSelectModel, allValues: CidadaoFormModel) =>
        racaCor?.value !== RacaCorDbEnum.INDIGENA ? null : allValues?.dadosPessoais?.etnia,
    },
  },
]

const createNomeGenitoresCalculator = (meta: MetaRoot<CidadaoFormModel>): Calculation[] => [
  {
    field: meta?.dadosPessoais?.desconheceNomeMae.absolutePath(),
    updates: {
      [meta?.dadosPessoais?.nomeMae.absolutePath()]: (desconheceNomeMae: boolean, allValues: CidadaoFormModel) =>
        desconheceNomeMae ? null : allValues?.dadosPessoais?.nomeMae,
    },
  },
  {
    field: meta?.dadosPessoais?.desconheceNomePai.absolutePath(),
    updates: {
      [meta?.dadosPessoais?.nomePai.absolutePath()]: (desconheceNomePai: boolean, allValues: CidadaoFormModel) =>
        desconheceNomePai ? null : allValues?.dadosPessoais?.nomePai,
    },
  },
]

const createIdentidadeGeneroCalculator = (meta: MetaRoot<CidadaoFormModel>): Calculation[] => [
  {
    field: meta?.dadosPessoais?.desejaInformarIdentidadeGenero.absolutePath(),
    updates: {
      [meta?.dadosPessoais?.identidadeGenero.absolutePath()]: (
        desejaInformarIdentidadeGenero: SimNaoEnum,
        allValues: CidadaoFormModel
      ) => (desejaInformarIdentidadeGenero !== SimNaoEnum.SIM ? null : allValues?.dadosPessoais?.identidadeGenero),
    },
  },
]

const createOrientacaoSexualCalculator = (meta: MetaRoot<CidadaoFormModel>): Calculation[] => [
  {
    field: meta?.dadosPessoais?.desejaInformarOrientacaoSexual.absolutePath(),
    updates: {
      [meta?.dadosPessoais?.orientacaoSexual.absolutePath()]: (
        desejaInformarOrientacaoSexual: SimNaoEnum,
        allValues: CidadaoFormModel
      ) => (desejaInformarOrientacaoSexual !== SimNaoEnum.SIM ? null : allValues?.dadosPessoais?.orientacaoSexual),
    },
  },
]

const createNacionalidadeCalculator = (meta: MetaRoot<CidadaoFormModel>): Calculation[] => [
  {
    field: meta?.dadosPessoais?.nacionalidadeCidadao?.nacionalidade.absolutePath(),
    updates: {
      [meta?.dadosPessoais?.nacionalidadeCidadao?.municipioNascimento.absolutePath()]: (
        nacionalidadeEnum: NacionalidadeEnum,
        allValues: CidadaoFormModel
      ) =>
        nacionalidadeEnum !== NacionalidadeEnum.BRASILEIRA
          ? null
          : allValues?.dadosPessoais?.nacionalidadeCidadao?.municipioNascimento,
      [meta?.cidadaoAldeado?.aldeiaNascimento.absolutePath()]: (
        nacionalidadeEnum: NacionalidadeEnum,
        allValues: CidadaoFormModel
      ) => {
        if (!allValues?.dadosPessoais?.isCidadaoAldeado) return undefined
        return nacionalidadeEnum !== NacionalidadeEnum.BRASILEIRA ? null : allValues?.cidadaoAldeado?.aldeiaNascimento
      },
      [meta?.cidadaoAldeado?.localOcorrencia.absolutePath()]: (
        nacionalidadeEnum: NacionalidadeEnum,
        allValues: CidadaoFormModel
      ) => {
        if (!allValues?.dadosPessoais?.isCidadaoAldeado) return undefined
        return nacionalidadeEnum !== NacionalidadeEnum.BRASILEIRA ? null : allValues?.cidadaoAldeado?.localOcorrencia
      },
      [meta?.cidadaoAldeado?.tipoLocalNascimento.absolutePath()]: (
        nacionalidadeEnum: NacionalidadeEnum,
        allValues: CidadaoFormModel
      ) => {
        if (!allValues?.dadosPessoais?.isCidadaoAldeado) return undefined
        return nacionalidadeEnum !== NacionalidadeEnum.BRASILEIRA
          ? null
          : allValues?.cidadaoAldeado?.tipoLocalNascimento
      },
      [meta?.dadosPessoais?.nacionalidadeCidadao?.portariaNaturalizacao.absolutePath()]: (
        nacionalidadeEnum: NacionalidadeEnum,
        allValues: CidadaoFormModel
      ) =>
        nacionalidadeEnum !== NacionalidadeEnum.NATURALIZADA
          ? null
          : allValues?.dadosPessoais?.nacionalidadeCidadao?.portariaNaturalizacao,
      [meta?.dadosPessoais?.nacionalidadeCidadao?.dataNaturalizacao.absolutePath()]: (
        nacionalidadeEnum: NacionalidadeEnum,
        allValues: CidadaoFormModel
      ) =>
        nacionalidadeEnum !== NacionalidadeEnum.NATURALIZADA
          ? null
          : allValues?.dadosPessoais?.nacionalidadeCidadao?.dataNaturalizacao,

      [meta?.dadosPessoais?.nacionalidadeCidadao?.paisNascimento.absolutePath()]: (
        nacionalidadeEnum: NacionalidadeEnum,
        allValues: CidadaoFormModel
      ) =>
        nacionalidadeEnum !== NacionalidadeEnum.ESTRANGEIRA
          ? null
          : allValues?.dadosPessoais?.nacionalidadeCidadao?.paisNascimento,
      [meta?.dadosPessoais?.nacionalidadeCidadao?.dataEntradaPais.absolutePath()]: (
        nacionalidadeEnum: NacionalidadeEnum,
        allValues: CidadaoFormModel
      ) =>
        nacionalidadeEnum !== NacionalidadeEnum.ESTRANGEIRA
          ? null
          : allValues?.dadosPessoais?.nacionalidadeCidadao?.dataEntradaPais,
    },
  },
]

const createObitoCalculator = (meta: MetaRoot<CidadaoFormModel>): Calculation[] => [
  {
    field: meta?.dadosPessoais?.faleceu?.absolutePath(),
    updates: {
      [meta?.dadosPessoais?.dataObito?.absolutePath()]: (faleceu: boolean, allValues: CidadaoFormModel) =>
        faleceu !== true ? null : allValues?.dadosPessoais?.dataObito,
      [meta?.dadosPessoais?.numeroDeclaracaoObito?.absolutePath()]: (faleceu: boolean, allValues: CidadaoFormModel) =>
        faleceu !== true ? null : allValues?.dadosPessoais?.numeroDeclaracaoObito,
    },
  },
]

const createInformacoesSociodemograficasCalculator = (meta: MetaRoot<CidadaoFormModel>): Calculation[] => [
  {
    field: meta?.informacoesSociodemograficas?.possuiDeficiencia.absolutePath(),
    updates: {
      [meta?.informacoesSociodemograficas.deficiencias.absolutePath()]: (
        possuiDeficiencia: SimNaoEnum,
        allValues: CidadaoFormModel
      ) => (possuiDeficiencia !== SimNaoEnum.SIM ? null : allValues.informacoesSociodemograficas.deficiencias),
    },
  },
  {
    field: meta?.informacoesSociodemograficas?.isMembroComunidadeTradicional.absolutePath(),
    updates: {
      [meta?.informacoesSociodemograficas.povoComunidadeTradicional.absolutePath()]: (
        isMembroComunidadeTradicional: SimNaoEnum,
        allValues: CidadaoFormModel
      ) =>
        isMembroComunidadeTradicional !== SimNaoEnum.SIM
          ? null
          : allValues.informacoesSociodemograficas.povoComunidadeTradicional,
    },
  },
]

const createAtendimentoSocioeducativoCalculator = (meta: MetaRoot<CidadaoFormModel>): Calculation[] => [
  {
    field: meta?.informacoesSociodemograficas?.isAtendimentoSocioeducativo.absolutePath(),
    updates: {
      [meta?.informacoesSociodemograficas?.tipoAtendimentoSocioeducativo.absolutePath()]: (
        isAtendimentoSocioeducativo: SimNaoEnum,
        allValues: CidadaoFormModel
      ) =>
        isAtendimentoSocioeducativo !== SimNaoEnum.SIM
          ? null
          : allValues.informacoesSociodemograficas?.tipoAtendimentoSocioeducativo,
      [meta?.informacoesSociodemograficas?.tempoAtendimentoSocioeducativo.absolutePath()]: (
        isAtendimentoSocioeducativo: SimNaoEnum,
        allValues: CidadaoFormModel
      ) =>
        isAtendimentoSocioeducativo !== SimNaoEnum.SIM
          ? null
          : allValues.informacoesSociodemograficas?.tempoAtendimentoSocioeducativo,
    },
  },
  {
    field: meta?.dadosPessoais?.dataNascimento.absolutePath(),
    updates: {
      [meta?.informacoesSociodemograficas?.isAtendimentoSocioeducativo.absolutePath()]: (
        dataNascimento: string,
        allValues: CidadaoFormModel
      ) =>
        !isCidadaoEntre12E21Anos(dataNascimento)
          ? null
          : allValues?.informacoesSociodemograficas?.isAtendimentoSocioeducativo,
      [meta?.informacoesSociodemograficas?.tipoAtendimentoSocioeducativo.absolutePath()]: (
        dataNascimento: string,
        allValues: CidadaoFormModel
      ) =>
        !isCidadaoEntre12E21Anos(dataNascimento)
          ? null
          : allValues?.informacoesSociodemograficas?.tipoAtendimentoSocioeducativo,
      [meta?.informacoesSociodemograficas?.tempoAtendimentoSocioeducativo.absolutePath()]: (
        dataNascimento: string,
        allValues: CidadaoFormModel
      ) =>
        !isCidadaoEntre12E21Anos(dataNascimento)
          ? null
          : allValues?.informacoesSociodemograficas?.tempoAtendimentoSocioeducativo,
    },
  },
]

const createCondicoesSaudeAutorreferidasCalculator = (meta: MetaRoot<CidadaoFormModel>): Calculation[] => [
  {
    field: meta?.dadosPessoais.sexo.absolutePath(),
    updates: {
      [meta?.condicoesSaudeAutorreferidas.stGestante.absolutePath()]: (sexo: string, allValues: CidadaoFormModel) =>
        sexo === SexoEnum.MASCULINO ? null : allValues.condicoesSaudeAutorreferidas?.stGestante,

      [meta?.condicoesSaudeAutorreferidas.maternidadeReferencia.absolutePath()]: (
        sexo: string,
        allValues: CidadaoFormModel
      ) => (sexo === SexoEnum.MASCULINO ? null : allValues.condicoesSaudeAutorreferidas?.maternidadeReferencia),
    },
  },
  {
    field: meta?.condicoesSaudeAutorreferidas.stGestante.absolutePath(),
    updates: {
      [meta?.condicoesSaudeAutorreferidas.maternidadeReferencia.absolutePath()]: (
        stGestante: SimNaoEnum,
        allValues: CidadaoFormModel
      ) => (stGestante !== SimNaoEnum.SIM ? null : allValues.condicoesSaudeAutorreferidas?.maternidadeReferencia),
    },
  },
  {
    field: meta?.condicoesSaudeAutorreferidas.stDoencaCardiaca.absolutePath(),
    updates: {
      [meta?.condicoesSaudeAutorreferidas.doencaCardiaca.absolutePath()]: (
        stDoencaCardiaca: SimNaoEnum,
        allValues: CidadaoFormModel
      ) => (stDoencaCardiaca !== SimNaoEnum.SIM ? null : allValues.condicoesSaudeAutorreferidas?.doencaCardiaca),
    },
  },
  {
    field: meta?.dadosPessoais?.dataNascimento.absolutePath(),
    updates: {
      [meta?.condicoesSaudeAutorreferidas.stGestante.absolutePath()]: (
        dataNascimento: string,
        allValues: CidadaoFormModel
      ) => {
        const idade = dataNascimento ? differenceInYears(new Date(), new Date(dataNascimento)) : 0
        return idade < 9 || idade > 60 ? null : allValues?.condicoesSaudeAutorreferidas?.stGestante
      },
      [meta?.condicoesSaudeAutorreferidas.maternidadeReferencia.absolutePath()]: (
        dataNascimento: string,
        allValues: CidadaoFormModel
      ) => {
        const idade = dataNascimento ? differenceInYears(new Date(), new Date(dataNascimento)) : 0
        return idade < 9 || idade > 60 ? null : allValues?.condicoesSaudeAutorreferidas?.maternidadeReferencia
      },
      [meta?.informacoesSociodemograficas.criancas0a9anosComQuemFica.absolutePath()]: (
        dataNascimento: string,
        allValues: CidadaoFormModel
      ) => {
        const idade = dataNascimento ? differenceInYears(new Date(), new Date(dataNascimento)) : 0
        return idade > 9 ? null : allValues?.informacoesSociodemograficas?.criancas0a9anosComQuemFica
      },
    },
  },
  {
    field: meta?.condicoesSaudeAutorreferidas.stDoencaRespiratoria.absolutePath(),
    updates: {
      [meta?.condicoesSaudeAutorreferidas.doencasRespiratorias.absolutePath()]: (
        stDoencaRespiratoria: SimNaoEnum,
        allValues: CidadaoFormModel
      ) =>
        stDoencaRespiratoria !== SimNaoEnum.SIM ? null : allValues.condicoesSaudeAutorreferidas?.doencasRespiratorias,
    },
  },
  {
    field: meta?.condicoesSaudeAutorreferidas.stProblemaRins.absolutePath(),
    updates: {
      [meta?.condicoesSaudeAutorreferidas.doencaRins.absolutePath()]: (
        stProblemaRins: SimNaoEnum,
        allValues: CidadaoFormModel
      ) => (stProblemaRins !== SimNaoEnum.SIM ? null : allValues.condicoesSaudeAutorreferidas?.doencaRins),
    },
  },
  {
    field: meta?.condicoesSaudeAutorreferidas.stInternacaoUltimos12Meses.absolutePath(),
    updates: {
      [meta?.condicoesSaudeAutorreferidas.causaInternacaoUltimos12Meses.absolutePath()]: (
        stInternacaoUltimos12Meses: SimNaoEnum,
        allValues: CidadaoFormModel
      ) =>
        stInternacaoUltimos12Meses !== SimNaoEnum.SIM
          ? null
          : allValues.condicoesSaudeAutorreferidas?.causaInternacaoUltimos12Meses,
    },
  },
  {
    field: meta?.condicoesSaudeAutorreferidas.stPlantasMedicinais.absolutePath(),
    updates: {
      [meta?.condicoesSaudeAutorreferidas.plantasMedicinais.absolutePath()]: (
        stPlantasMedicinais: SimNaoEnum,
        allValues: CidadaoFormModel
      ) => (stPlantasMedicinais !== SimNaoEnum.SIM ? null : allValues.condicoesSaudeAutorreferidas?.plantasMedicinais),
    },
  },
]

const createSituacaoRuaCalculator = (meta: MetaRoot<CidadaoFormModel>): Calculation[] => {
  return [
    {
      field: meta?.statusSituacaoRua.absolutePath(),
      updates: {
        [meta?.situacaoRua?.absolutePath()]: (statusSituacaoRua: SimNaoEnum, allValues: CidadaoFormModel) =>
          statusSituacaoRua !== SimNaoEnum.SIM ? null : allValues.situacaoRua,
      },
    },
    {
      field: meta?.situacaoRua?.possuiAcessoHigienePessoal.absolutePath(),
      updates: {
        [meta?.situacaoRua?.higienePessoal?.absolutePath()]: (
          possuiHigienePessoal: SimNaoEnum,
          allValues: CidadaoFormModel
        ) =>
          allValues?.situacaoRua && possuiHigienePessoal !== SimNaoEnum.SIM
            ? null
            : allValues?.situacaoRua?.higienePessoal,
      },
    },
    {
      field: meta?.situacaoRua?.stAcompanhadoOutraInstituicao.absolutePath(),
      updates: {
        [meta?.situacaoRua?.instituicaoAcompanhamento?.absolutePath()]: (
          stAcompanhadoOutraInstituicao: SimNaoEnum,
          allValues: CidadaoFormModel
        ) =>
          allValues?.situacaoRua && stAcompanhadoOutraInstituicao !== SimNaoEnum.SIM
            ? null
            : allValues?.situacaoRua?.instituicaoAcompanhamento,
      },
    },
    {
      field: meta?.situacaoRua?.visitaFamiliarComFrequencia.absolutePath(),
      updates: {
        [meta?.situacaoRua?.grauParentescoVisitaFamiliar?.absolutePath()]: (
          visitaFamiliarComFrequencia: SimNaoEnum,
          allValues: CidadaoFormModel
        ) =>
          allValues?.situacaoRua && visitaFamiliarComFrequencia !== SimNaoEnum.SIM
            ? null
            : allValues?.situacaoRua?.grauParentescoVisitaFamiliar,
      },
    },
  ]
}

const createResponsabilidadeAcompanhamentoCalculator = (meta: MetaRoot<CidadaoFormModel>): Calculation[] => {
  return [
    {
      field: meta?.responsabilidadeAcompanhamento.statusForaArea.absolutePath(),
      updates: {
        [meta?.responsabilidadeAcompanhamento?.microarea.absolutePath()]: (
          statusForaArea: boolean,
          allValues: CidadaoFormModel
        ) => (statusForaArea === true ? null : allValues?.responsabilidadeAcompanhamento?.microarea),
      },
    },
    {
      field: meta?.dadosPessoais?.faleceu?.absolutePath(),
      updates: {
        [meta?.responsabilidadeAcompanhamento?.statusCidadaoSaiuTerritorio.absolutePath()]: (
          faleceu: boolean,
          allValues: CidadaoFormModel
        ) => (faleceu === true ? null : allValues?.responsabilidadeAcompanhamento?.statusCidadaoSaiuTerritorio),
      },
    },
    {
      field: meta?.responsabilidadeAcompanhamento.isResponsavelFamiliar.absolutePath(),
      updates: {
        [meta?.responsabilidadeAcompanhamento.responsavelFamiliar.absolutePath()]: (
          isResponsavelFamiliar: boolean,
          allValues: CidadaoFormModel
        ) => (isResponsavelFamiliar === true ? null : allValues?.responsabilidadeAcompanhamento?.responsavelFamiliar),

        [meta?.responsabilidadeAcompanhamento.grauParentescoResponsavelFamiliar.absolutePath()]: (
          isResponsavelFamiliar: boolean,
          allValues: CidadaoFormModel
        ) =>
          isResponsavelFamiliar === true
            ? null
            : allValues?.responsabilidadeAcompanhamento?.grauParentescoResponsavelFamiliar,
      },
    },
    {
      field: meta?.responsabilidadeAcompanhamento.responsavelFamiliar.absolutePath(),
      updates: {
        [meta?.responsabilidadeAcompanhamento.grauParentescoResponsavelFamiliar.absolutePath()]: (
          responsavelFamiliar: any,
          allValues: CidadaoFormModel
        ) =>
          !responsavelFamiliar ? null : allValues?.responsabilidadeAcompanhamento?.grauParentescoResponsavelFamiliar,
      },
    },
  ]
}

const createTipoLocalNascimentoCalculator = (meta: MetaRoot<CidadaoFormModel>): Calculation[] => {
  return [
    {
      field: meta?.cidadaoAldeado?.tipoLocalNascimento?.absolutePath(),
      updates: {
        [meta?.cidadaoAldeado?.aldeiaNascimento?.absolutePath()]: (
          tipoLocalNascimento: TipoLocalNascimentoEnum,
          allValues: CidadaoFormModel
        ) => {
          if (!allValues?.dadosPessoais?.isCidadaoAldeado) return undefined
          return tipoLocalNascimento !== TipoLocalNascimentoEnum.ALDEIA
            ? null
            : allValues.cidadaoAldeado.aldeiaNascimento
        },

        [meta?.cidadaoAldeado?.localOcorrencia?.absolutePath()]: (
          tipoLocalNascimento: TipoLocalNascimentoEnum,
          allValues: CidadaoFormModel
        ) => {
          if (!allValues?.dadosPessoais?.isCidadaoAldeado) return undefined
          return tipoLocalNascimento !== TipoLocalNascimentoEnum.ALDEIA
            ? null
            : allValues.cidadaoAldeado.localOcorrencia
        },

        [meta?.dadosPessoais?.nacionalidadeCidadao?.municipioNascimento?.absolutePath()]: (
          tipoLocalNascimento: TipoLocalNascimentoEnum,
          allValues: CidadaoFormModel
        ) =>
          tipoLocalNascimento !== TipoLocalNascimentoEnum.ALDEIA
            ? allValues?.dadosPessoais?.nacionalidadeCidadao?.municipioNascimento
            : null,
      },
    },
  ]
}

const createCidadaoAldeadoCalculator = (meta: MetaRoot<CidadaoFormModel>): Calculation[] => [
  {
    field: meta?.dadosPessoais?.isCidadaoAldeado?.absolutePath(),
    updates: {
      [meta.tipoEndereco.absolutePath()]: (isCidadaoAldeado: boolean, allValues: CidadaoFormModel) =>
        !isCidadaoAldeado ? TipoEnderecoEnum.LOGRADOURO : allValues.tipoEndereco,

      [meta.cidadaoAldeado.absolutePath()]: (isCidadaoAldeado: boolean, allValues: CidadaoFormModel) =>
        !isCidadaoAldeado ? null : allValues.cidadaoAldeado,

      [meta.cidadaoAldeado.tipoLocalNascimento.absolutePath()]: (
        isCidadaoAldeado: boolean,
        allValues: CidadaoFormModel
      ) => {
        if (!isCidadaoAldeado) return undefined

        const isBrasileira =
          allValues.dadosPessoais?.nacionalidadeCidadao?.nacionalidade === NacionalidadeEnum.BRASILEIRA

        if (!isBrasileira) {
          return null
        }

        if (allValues.cidadaoAldeado?.tipoLocalNascimento === TipoLocalNascimentoEnum.ALDEIA) {
          return TipoLocalNascimentoEnum.ALDEIA
        }

        const hasMunicipioNascimento = !!allValues.dadosPessoais?.nacionalidadeCidadao?.municipioNascimento

        if (isBrasileira && hasMunicipioNascimento) {
          return TipoLocalNascimentoEnum.MUNICIPIO
        }

        return allValues.cidadaoAldeado?.tipoLocalNascimento
      },
    },
  },
  {
    field: meta?.dadosPessoais?.nacionalidadeCidadao?.municipioNascimento?.absolutePath(),
    updates: {
      [meta.cidadaoAldeado.tipoLocalNascimento.absolutePath()]: (
        municipioNascimento: MunicipioSelectModel,
        allValues: CidadaoFormModel
      ) => {
        const isBrasileira =
          allValues.dadosPessoais?.nacionalidadeCidadao?.nacionalidade === NacionalidadeEnum.BRASILEIRA

        if (!isBrasileira) {
          return null
        }

        if (allValues.cidadaoAldeado?.tipoLocalNascimento === TipoLocalNascimentoEnum.ALDEIA) {
          return TipoLocalNascimentoEnum.ALDEIA
        }

        const isCidadaoAldeado = allValues.dadosPessoais?.isCidadaoAldeado

        if (isCidadaoAldeado && !!municipioNascimento) {
          return TipoLocalNascimentoEnum.MUNICIPIO
        }

        return allValues.cidadaoAldeado?.tipoLocalNascimento
      },
    },
  },
]
const enderecoIndigenaCalculator = (meta: MetaRoot<EnderecoIndigenaFieldGroupModel>): Calculation[] => [
  {
    field: meta.dsei.absolutePath(),
    updates: {
      [meta.poloBase.absolutePath()]: (dsei: DseiSelectFieldModel, allValues: CidadaoFormModel) =>
        dsei?.id !== allValues?.enderecoIndigena?.poloBase?.dsei.id ? null : allValues?.enderecoIndigena?.poloBase,
    },
  },
  {
    field: meta.poloBase.absolutePath(),
    updates: {
      [meta.dsei.absolutePath()]: (poloBase: PoloBaseSelectFieldModel, allValues: CidadaoFormModel) =>
        poloBase?.dsei ? poloBase.dsei : allValues?.enderecoIndigena?.dsei,
      [meta.aldeia.absolutePath()]: (poloBase: PoloBaseSelectFieldModel, allValues: CidadaoFormModel) =>
        poloBase?.id !== allValues?.enderecoIndigena?.aldeia?.poloBase?.id ? null : allValues?.enderecoIndigena?.aldeia,
    },
  },
  {
    field: meta.aldeia.absolutePath(),
    updates: {
      [meta.poloBase.absolutePath()]: (aldeia: AldeiaSelectFieldModel, allValues: CidadaoFormModel) =>
        aldeia?.poloBase ? aldeia.poloBase : allValues?.enderecoIndigena?.poloBase,
      [meta.dsei.absolutePath()]: (aldeia: AldeiaSelectFieldModel, allValues: CidadaoFormModel) =>
        aldeia?.poloBase?.dsei ? aldeia.poloBase.dsei : allValues?.enderecoIndigena?.dsei,
      [meta.municipio.absolutePath()]: (aldeia: AldeiaSelectFieldModel) => {
        if (aldeia?.id) {
          if (aldeia.municipio.length > 1) {
            return null
          } else {
            return aldeia.municipio[0]
          }
        } else return undefined
      },
      [meta.uf.absolutePath()]: (aldeia: AldeiaSelectFieldModel) => {
        if (aldeia?.id) {
          if (aldeia.municipio.length > 1) {
            return null
          } else {
            return aldeia.municipio[0].uf
          }
        } else return undefined
      },
    },
  },
  {
    field: meta.municipio.absolutePath(),
    updates: {
      [meta.uf.absolutePath()]: (municipio: MunicipioSelectModel) => (municipio?.uf ? municipio.uf : undefined),
    },
  },
]

export const enderecoCalculator = (path: MetaRoot<EnderecoFieldGroupModel>): Calculation[] => [
  {
    field: path.pais.absolutePath(),
    updates: {
      [path.cep.absolutePath()]: (value: PaisSelectFieldModel, values: EnderecoFieldGroupModel) => {
        const endereco = resolveValue(values, path)
        return value?.id !== CODIGO_PAIS_BRASIL ? null : endereco.cep
      },
      [path.uf.absolutePath()]: (value: PaisSelectFieldModel, values: EnderecoFieldGroupModel) => {
        const endereco = resolveValue(values, path)
        return value?.id !== CODIGO_PAIS_BRASIL ? null : endereco.uf
      },
      [path.municipio.absolutePath()]: (value: PaisSelectFieldModel, values: EnderecoFieldGroupModel) => {
        const endereco = resolveValue(values, path)
        return value?.id !== CODIGO_PAIS_BRASIL ? null : endereco.municipio
      },
      [path.bairro.absolutePath()]: (value: PaisSelectFieldModel, values: EnderecoFieldGroupModel) => {
        const endereco = resolveValue(values, path)
        return value?.id !== CODIGO_PAIS_BRASIL ? null : endereco.bairro
      },
      [path.tipoLogradouro.absolutePath()]: (value: PaisSelectFieldModel, values: EnderecoFieldGroupModel) => {
        const endereco = resolveValue(values, path)
        return value?.id !== CODIGO_PAIS_BRASIL ? null : endereco.tipoLogradouro
      },
      [path.logradouro.absolutePath()]: (value: PaisSelectFieldModel, values: EnderecoFieldGroupModel) => {
        const endereco = resolveValue(values, path)
        return value?.id !== CODIGO_PAIS_BRASIL ? null : endereco.logradouro
      },
      [path.numero.absolutePath()]: (value: PaisSelectFieldModel, values: EnderecoFieldGroupModel) => {
        const endereco = resolveValue(values, path)
        return value?.id !== CODIGO_PAIS_BRASIL ? null : endereco.numero
      },
      [path.semNumero.absolutePath()]: (value: PaisSelectFieldModel, values: EnderecoFieldGroupModel) => {
        const endereco = resolveValue(values, path)
        return value?.id !== CODIGO_PAIS_BRASIL ? null : endereco.semNumero
      },
      [path.complemento.absolutePath()]: (value: PaisSelectFieldModel, values: EnderecoFieldGroupModel) => {
        const endereco = resolveValue(values, path)
        return value?.id !== CODIGO_PAIS_BRASIL ? null : endereco.complemento
      },
      [path.pontoReferencia.absolutePath()]: (value: PaisSelectFieldModel, values: EnderecoFieldGroupModel) => {
        const endereco = resolveValue(values, path)
        return value?.id !== CODIGO_PAIS_BRASIL ? null : endereco.pontoReferencia
      },
      [path.area.absolutePath()]: (value: PaisSelectFieldModel, values: EnderecoFieldGroupModel) => {
        const endereco = resolveValue(values, path)
        return value?.id !== CODIGO_PAIS_BRASIL ? null : endereco.area
      },
      [path.microArea.absolutePath()]: (value: PaisSelectFieldModel, values: EnderecoFieldGroupModel) => {
        const endereco = resolveValue(values, path)
        return value?.id !== CODIGO_PAIS_BRASIL ? null : endereco.microArea
      },
      [path.foraArea.absolutePath()]: (value: PaisSelectFieldModel, values: EnderecoFieldGroupModel) => {
        const endereco = resolveValue(values, path)
        return value?.id !== CODIGO_PAIS_BRASIL ? null : endereco.foraArea
      },
      [path.municipioResidenciaExterior.absolutePath()]: (
        value: PaisSelectFieldModel,
        values: EnderecoFieldGroupModel
      ) => {
        const endereco = resolveValue(values, path)
        return value?.id === CODIGO_PAIS_BRASIL ? null : endereco.municipioResidenciaExterior
      },
    },
  },
  {
    field: path.semNumero.absolutePath(),
    updates: {
      [path.numero.absolutePath()]: (semNumero: boolean) => (semNumero ? 'S/N' : null),
    },
  },
]

const clearEnderecoCalculator = (meta: MetaRoot<CidadaoFormModel>): Calculation[] => {
  const metaEndereco = meta.endereco
  const metaEnderecoIndigena = meta.enderecoIndigena

  return [
    {
      field: meta.tipoEndereco.absolutePath(),
      updates: {
        [metaEndereco.pais.absolutePath()]: (tipoEndereco: TipoEnderecoEnum, values: CidadaoFormModel) => {
          const endereco = resolveValue(values, metaEndereco)
          if (tipoEndereco !== TipoEnderecoEnum.LOGRADOURO) {
            return null
          } else {
            return endereco.pais || { id: CODIGO_PAIS_BRASIL, nome: 'BRASIL' }
          }
        },
        [metaEndereco.cep.absolutePath()]: (tipoEndereco: TipoEnderecoEnum, values: CidadaoFormModel) => {
          const endereco = resolveValue(values, metaEndereco)
          return tipoEndereco !== TipoEnderecoEnum.LOGRADOURO ? null : endereco.cep
        },
        [metaEndereco.uf.absolutePath()]: (tipoEndereco: TipoEnderecoEnum, values: CidadaoFormModel) => {
          const endereco = resolveValue(values, metaEndereco)
          return tipoEndereco !== TipoEnderecoEnum.LOGRADOURO ? null : endereco.uf
        },
        [metaEndereco.municipio.absolutePath()]: (tipoEndereco: TipoEnderecoEnum, values: CidadaoFormModel) => {
          const endereco = resolveValue(values, metaEndereco)
          return tipoEndereco !== TipoEnderecoEnum.LOGRADOURO ? null : endereco.municipio
        },
        [metaEndereco.bairro.absolutePath()]: (tipoEndereco: TipoEnderecoEnum, values: CidadaoFormModel) => {
          const endereco = resolveValue(values, metaEndereco)
          return tipoEndereco !== TipoEnderecoEnum.LOGRADOURO ? null : endereco.bairro
        },
        [metaEndereco.tipoLogradouro.absolutePath()]: (tipoEndereco: TipoEnderecoEnum, values: CidadaoFormModel) => {
          const endereco = resolveValue(values, metaEndereco)
          return tipoEndereco !== TipoEnderecoEnum.LOGRADOURO ? null : endereco.tipoLogradouro
        },
        [metaEndereco.logradouro.absolutePath()]: (tipoEndereco: TipoEnderecoEnum, values: CidadaoFormModel) => {
          const endereco = resolveValue(values, metaEndereco)
          return tipoEndereco !== TipoEnderecoEnum.LOGRADOURO ? null : endereco.logradouro
        },
        [metaEndereco.numero.absolutePath()]: (tipoEndereco: TipoEnderecoEnum, values: CidadaoFormModel) => {
          const endereco = resolveValue(values, metaEndereco)
          return tipoEndereco !== TipoEnderecoEnum.LOGRADOURO ? null : endereco.numero
        },
        [metaEndereco.semNumero.absolutePath()]: (tipoEndereco: TipoEnderecoEnum, values: CidadaoFormModel) => {
          const endereco = resolveValue(values, metaEndereco)
          return tipoEndereco !== TipoEnderecoEnum.LOGRADOURO ? null : endereco.semNumero
        },
        [metaEndereco.complemento.absolutePath()]: (tipoEndereco: TipoEnderecoEnum, values: CidadaoFormModel) => {
          const endereco = resolveValue(values, metaEndereco)
          return tipoEndereco !== TipoEnderecoEnum.LOGRADOURO ? null : endereco.complemento
        },
        [metaEndereco.pontoReferencia.absolutePath()]: (tipoEndereco: TipoEnderecoEnum, values: CidadaoFormModel) => {
          const endereco = resolveValue(values, metaEndereco)
          return tipoEndereco !== TipoEnderecoEnum.LOGRADOURO ? null : endereco.pontoReferencia
        },
        [metaEndereco.area.absolutePath()]: (tipoEndereco: TipoEnderecoEnum, values: CidadaoFormModel) => {
          const endereco = resolveValue(values, metaEndereco)
          return tipoEndereco !== TipoEnderecoEnum.LOGRADOURO ? null : endereco.area
        },
        [metaEndereco.microArea.absolutePath()]: (tipoEndereco: TipoEnderecoEnum, values: CidadaoFormModel) => {
          const endereco = resolveValue(values, metaEndereco)
          return tipoEndereco !== TipoEnderecoEnum.LOGRADOURO ? null : endereco.microArea
        },
        [metaEndereco.foraArea.absolutePath()]: (tipoEndereco: TipoEnderecoEnum, values: CidadaoFormModel) => {
          const endereco = resolveValue(values, metaEndereco)
          return tipoEndereco !== TipoEnderecoEnum.LOGRADOURO ? null : endereco.foraArea
        },
        [metaEndereco.municipioResidenciaExterior.absolutePath()]: (
          tipoEndereco: TipoEnderecoEnum,
          values: CidadaoFormModel
        ) => {
          const endereco = resolveValue(values, metaEndereco)
          return tipoEndereco !== TipoEnderecoEnum.LOGRADOURO ? null : endereco.municipioResidenciaExterior
        },
        [metaEndereco.numeroFamilia.absolutePath()]: (tipoEndereco: TipoEnderecoEnum, values: CidadaoFormModel) => {
          const endereco = resolveValue(values, metaEndereco)
          return tipoEndereco !== TipoEnderecoEnum.LOGRADOURO ? null : endereco.numeroFamilia
        },
        [metaEnderecoIndigena.aldeia.absolutePath()]: (tipoEndereco: TipoEnderecoEnum, values: CidadaoFormModel) => {
          const enderecoIndigena = resolveValue(values, metaEnderecoIndigena)
          return !tipoEnderecoIndigena.includes(tipoEndereco) ? null : enderecoIndigena?.aldeia
        },
        [metaEnderecoIndigena.dsei.absolutePath()]: (tipoEndereco: TipoEnderecoEnum, values: CidadaoFormModel) => {
          const enderecoIndigena = resolveValue(values, metaEnderecoIndigena)
          return !tipoEnderecoIndigena.includes(tipoEndereco) ? null : enderecoIndigena?.dsei
        },
        [metaEnderecoIndigena.poloBase.absolutePath()]: (tipoEndereco: TipoEnderecoEnum, values: CidadaoFormModel) => {
          const enderecoIndigena = resolveValue(values, metaEnderecoIndigena)
          return !tipoEnderecoIndigena.includes(tipoEndereco) ? null : enderecoIndigena?.poloBase
        },
        [metaEnderecoIndigena.uf.absolutePath()]: (tipoEndereco: TipoEnderecoEnum, values: CidadaoFormModel) => {
          const enderecoIndigena = resolveValue(values, metaEnderecoIndigena)
          return !tipoEnderecoIndigena.includes(tipoEndereco) ? null : enderecoIndigena?.uf
        },
        [metaEnderecoIndigena.municipio.absolutePath()]: (tipoEndereco: TipoEnderecoEnum, values: CidadaoFormModel) => {
          const enderecoIndigena = resolveValue(values, metaEnderecoIndigena)
          return !tipoEnderecoIndigena.includes(tipoEndereco) ? null : enderecoIndigena?.municipio
        },
        [metaEnderecoIndigena.numero.absolutePath()]: (tipoEndereco: TipoEnderecoEnum, values: CidadaoFormModel) => {
          const enderecoIndigena = resolveValue(values, metaEnderecoIndigena)
          return !tipoEnderecoIndigena.includes(tipoEndereco) ? null : enderecoIndigena?.numero
        },
        [metaEnderecoIndigena.numeroFamilia.absolutePath()]: (
          tipoEndereco: TipoEnderecoEnum,
          values: CidadaoFormModel
        ) => {
          const enderecoIndigena = resolveValue(values, metaEnderecoIndigena)
          return !tipoEnderecoIndigena.includes(tipoEndereco) ? null : enderecoIndigena?.numeroFamilia
        },
      },
    },
  ]
}

export const createEnderecoCalculator = (meta: MetaRoot<CidadaoFormModel>): Calculation[] => [
  ...enderecoCalculator(meta.endereco),
  ...enderecoIndigenaCalculator(meta.enderecoIndigena),
  ...clearEnderecoCalculator(meta),
]

export const createCidadaoCalculations = (meta: MetaRoot<CidadaoFormModel>): Calculation[] => [
  ...createEtniaCalculator(meta),
  ...createNomeGenitoresCalculator(meta),
  ...createIdentidadeGeneroCalculator(meta),
  ...createOrientacaoSexualCalculator(meta),
  ...createNacionalidadeCalculator(meta),
  ...createObitoCalculator(meta),
  ...createInformacoesSociodemograficasCalculator(meta),
  ...createAtendimentoSocioeducativoCalculator(meta),
  ...createCondicoesSaudeAutorreferidasCalculator(meta),
  ...createSituacaoRuaCalculator(meta),
  ...createResponsabilidadeAcompanhamentoCalculator(meta),
  ...createTipoLocalNascimentoCalculator(meta),
  ...createCidadaoAldeadoCalculator(meta),
  ...createCpfCalculator(meta),
  ...createCnsCalculator(meta),
  ...createEnderecoCalculator(meta),
]

export const createCidadaoCalculator = (meta: MetaRoot<CidadaoFormModel>) =>
  createDecorator(...createCidadaoCalculations(meta))
